import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

/**
 * PhoneShot — A brutalist phone-frame wrapper.
 * Modes:
 *   - `src` provided → renders a lazy-loaded product screenshot.
 *     On desktop (hover-capable pointer) the screenshot enlarges into a
 *     lightbox overlay when hovered, for better readability.
 *   - `Icon` provided → renders a stylised graphic placeholder with a large centred
 *     lucide icon over a dark concrete gradient with a neon yellow glow.
 *
 * Both modes share the same 2px yellow border, hard shadow, hazard bottom-strip
 * so cards remain visually balanced even when no photo is available.
 */
export const PhoneShot = ({ src, alt, testId, focus = "top", aspect = "portrait", Icon, iconLabel, zoomable = false }) => {
  const objectPosition = focus === "top" ? "top" : focus === "center" ? "center" : "top";
  const heightClass = aspect === "portrait" ? "h-[300px] md:h-[360px]" : "h-[220px]";
  const [zoomed, setZoomed] = useState(false);
  const canHoverRef = useRef(false);

  useEffect(() => {
    canHoverRef.current = window.matchMedia("(hover: hover) and (pointer: fine)").matches;
  }, []);

  useEffect(() => {
    if (!zoomed) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [zoomed]);

  const canZoom = zoomable && !!src;
  const handleEnter = () => {
    if (canZoom && canHoverRef.current) setZoomed(true);
  };
  const handleLeave = () => setZoomed(false);

  return (
    <>
      <div
        data-testid={testId}
        onMouseEnter={canZoom ? handleEnter : undefined}
        className={`relative w-full ${heightClass} border-2 border-[#FFEA00] bg-black overflow-hidden shadow-[6px_6px_0_0_rgba(255,234,0,0.18)] ${canZoom ? "cursor-zoom-in" : ""}`}
      >
        {/* Corner marker */}
        <div className="absolute top-0 right-0 w-2 h-2 bg-[#FFEA00] z-10" />

        {src ? (
          <>
            <img
              src={src}
              alt={alt}
              loading="lazy"
              decoding="async"
              className="w-full h-full object-cover"
              style={{ objectPosition }}
              draggable={false}
            />
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-0"
              style={{ background: "linear-gradient(180deg, rgba(0,0,0,0) 55%, rgba(0,0,0,0.55) 100%)" }}
            />
          </>
        ) : Icon ? (
          <PlaceholderTile Icon={Icon} label={iconLabel || alt} />
        ) : null}

        {/* Bottom hazard strip */}
        <div
          aria-hidden="true"
          className="absolute left-0 right-0 bottom-0 h-1.5"
          style={{
            backgroundImage:
              "repeating-linear-gradient(45deg, #FFEA00 0, #FFEA00 8px, #0a0a0a 8px, #0a0a0a 16px)",
          }}
        />
      </div>

      {zoomed && canZoom ? <PhoneShotLightbox src={src} alt={alt} onClose={handleLeave} /> : null}
    </>
  );
};

/** Fullscreen brutalist lightbox rendered via portal, closes on mouseleave / Esc. */
const PhoneShotLightbox = ({ src, alt, onClose }) => {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return createPortal(
    <div
      data-testid="phone-shot-lightbox"
      onMouseLeave={onClose}
      onClick={onClose}
      className="fixed inset-0 z-[9999] flex items-center justify-center animate-in fade-in duration-150"
      style={{
        background: "rgba(0,0,0,0.82)",
        backdropFilter: "blur(6px)",
        WebkitBackdropFilter: "blur(6px)",
      }}
    >
      {/* Frame */}
      <div
        className="relative border-2 border-[#FFEA00] bg-black shadow-[10px_10px_0_0_rgba(255,234,0,0.25)]"
        style={{ height: "80vh", maxHeight: "900px" }}
        onClick={(e) => e.stopPropagation()}
        onMouseLeave={onClose}
      >
        {/* Top hazard strip */}
        <div
          aria-hidden="true"
          className="absolute left-0 right-0 top-0 h-2"
          style={{
            backgroundImage:
              "repeating-linear-gradient(45deg, #FFEA00 0, #FFEA00 10px, #0a0a0a 10px, #0a0a0a 20px)",
          }}
        />
        <img
          src={src}
          alt={alt}
          className="block h-full w-auto select-none"
          draggable={false}
        />
        {/* Bottom hazard strip */}
        <div
          aria-hidden="true"
          className="absolute left-0 right-0 bottom-0 h-2"
          style={{
            backgroundImage:
              "repeating-linear-gradient(45deg, #FFEA00 0, #FFEA00 10px, #0a0a0a 10px, #0a0a0a 20px)",
          }}
        />
        {/* Corner marker */}
        <div className="absolute -top-1 -right-1 w-3 h-3 bg-[#FFEA00]" />
      </div>

      {/* Esc hint */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 font-mono text-[10px] tracking-[0.3em] text-[#A3A3A3] uppercase">
        Vie kursori pois kuvasta · Klikkaa taustaa · Esc sulkee
      </div>
    </div>,
    document.body
  );
};

/** Brutalist icon placeholder — used when no real screenshot exists yet. */
const PlaceholderTile = ({ Icon, label }) => {
  return (
    <div
      aria-label={label}
      className="absolute inset-0 flex flex-col items-center justify-center gap-4"
      style={{
        background:
          "radial-gradient(ellipse at 50% 40%, rgba(255,234,0,0.18) 0%, rgba(255,234,0,0.06) 35%, rgba(0,0,0,0) 70%), linear-gradient(135deg, #0a0a0a 0%, #141414 60%, #0a0a0a 100%)",
      }}
    >
      {/* Grid lines */}
      <div
        aria-hidden="true"
        className="absolute inset-0 opacity-30"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,234,0,0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(255,234,0,0.08) 1px, transparent 1px)",
          backgroundSize: "32px 32px",
        }}
      />
      {/* Corner ticks */}
      <div aria-hidden="true" className="absolute top-3 left-3 w-4 h-0.5 bg-[#FFEA00]" />
      <div aria-hidden="true" className="absolute top-3 left-3 w-0.5 h-4 bg-[#FFEA00]" />
      <div aria-hidden="true" className="absolute bottom-4 right-3 w-4 h-0.5 bg-[#FFEA00]" />
      <div aria-hidden="true" className="absolute bottom-4 right-3 w-0.5 h-4 bg-[#FFEA00]" />

      {/* Big icon in a brutalist frame */}
      <div className="relative w-24 h-24 md:w-28 md:h-28 border-2 border-[#FFEA00] bg-[#FFEA00]/[0.08] flex items-center justify-center shadow-[4px_4px_0_0_rgba(255,234,0,0.25)]">
        <div
          aria-hidden="true"
          className="absolute -inset-4 rounded-full"
          style={{
            background:
              "radial-gradient(circle, rgba(255,234,0,0.35) 0%, rgba(255,234,0,0) 70%)",
            filter: "blur(12px)",
          }}
        />
        <Icon className="relative w-12 h-12 md:w-14 md:h-14 text-[#FFEA00] stroke-[1.75]" />
      </div>

      {label && (
        <div className="relative font-mono text-[10px] md:text-[11px] tracking-[0.4em] uppercase text-[#FFEA00]/80">
          {label}
        </div>
      )}
    </div>
  );
};
